import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtIST } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TestBadge, LifecycleBadge, BookingStateBadge, IntentBadge, EmptyState } from "@/components/common";
import { KanbanSquare } from "lucide-react";

export default function OpportunitiesPage() {
  const [params] = useSearchParams();
  const [product, setProduct] = useState("all");
  const [status, setStatus] = useState(params.get("lifecycle_status") || "all");
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState(null);

  const load = useCallback(() => {
    const qp = new URLSearchParams();
    if (product !== "all") qp.set("product_code", product);
    if (status !== "all") qp.set("lifecycle_status", status);
    api.get(`/opportunities?${qp}`).then((r) => setItems(r.data));
  }, [product, status]);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="opportunities-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Opportunities</h1>
          <p className="text-sm text-muted-foreground">One product pursuit each — a customer can have several.</p>
        </div>
        <div className="flex gap-2">
          <Select value={product} onValueChange={setProduct}>
            <SelectTrigger data-testid="opps-product-filter" className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All products</SelectItem>
              {products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="opps-status-filter" className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="on_hold">On hold</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="closed_lost">Closed lost</SelectItem>
              <SelectItem value="discontinued">Discontinued</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card>
        {!items ? <div className="p-4 space-y-2">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10" />)}</div>
          : items.length === 0 ? <EmptyState icon={KanbanSquare} title="No opportunities match these filters" />
            : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Customer</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden md:table-cell">Booking</TableHead>
                    <TableHead className="hidden md:table-cell">Owner</TableHead>
                    <TableHead className="hidden lg:table-cell">Intent</TableHead>
                    <TableHead className="hidden lg:table-cell">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((o) => (
                    <TableRow key={o.opportunity_id} data-testid={`opp-row-${o.opportunity_id}`}>
                      <TableCell>
                        <Link to={`/customers/${o.contact_id}`} className="flex items-center gap-2 hover:underline">
                          <span className="font-medium">{o.contact?.full_name}</span>
                          <TestBadge show={o.is_test_record} />
                        </Link>
                        <span className="font-mono text-[11px] text-muted-foreground">{o.contact?.customer_id}</span>
                      </TableCell>
                      <TableCell className="text-sm">{o.product_name}</TableCell>
                      <TableCell className="text-sm">{o.current_stage_name || "—"}</TableCell>
                      <TableCell><LifecycleBadge status={o.lifecycle_status} /></TableCell>
                      <TableCell className="hidden md:table-cell"><BookingStateBadge state={o.booking_state} /></TableCell>
                      <TableCell className="hidden md:table-cell text-sm">{o.owner_name || <span className="text-muted-foreground">Pool</span>}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <IntentBadge category={o.intent_manual?.category || o.intent_ai?.category} source={o.intent_manual ? "manual" : (o.intent_ai ? "ai" : null)} />
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{fmtIST(o.created_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
      </Card>
    </div>
  );
}
